<template>
  <div class="body bank-app" :dir="isRTL ? 'rtl' : 'ltr'">

  <Navbar :show-back-button="true" @back="navigateToDashboard"/>

  <div class="account-summary">
    <div class="account-type">
      {{ $t('commitments.totalCommitments') }}
    </div>

    <div class="account-balance">
      <span class="balance-label"></span>
      <span class="balance-amount">
        {{ formatTotalTaahodat }} {{ getTotalCurrencySign() }}
      </span>
    </div>
  </div>


  <div class="section-header">
    <!-- <button class="back-arrow" @click="navigateToDashboard">→</button> -->

    <h3 class="section-title mt-3" style="margin-right: 10px;">
      {{ $t('commitments.listTitle') }}
    </h3>
  </div>


  <!-- CHARTS -->

  <div class="charts-section" v-if="filteredData.length > 0">

    <div class="charts-header">

      <h3 class="charts-title">
        📊 {{ $t('commitments.chartsTitle') }}
      </h3>

      <div class="chart-controls">

        <button
          class="chart-control-btn"
          @click="toggleCharts"
          :title="showCharts ? $t('commitments.hideCharts') : $t('commitments.showCharts')"
        >
          {{ showCharts ? $t('common.hide') : $t('common.show') }}
        </button>

        <select
          v-model="chartCurrencyFilter"
          class="chart-currency-select"
          @change="updateCharts"
        >
          <option value="all">{{ $t('currency.allCurrencies') }}</option>

          <option
            v-for="arz in arzList"
            :key="arz.idd"
            :value="arz.idd"
          >
            {{ arz.nam }}
          </option>

        </select>

      </div>
    </div>


    <div v-if="showCharts" class="charts-container">

      <div class="chart-type-selector">

        <button
          v-for="type in chartTypes"
          :key="type.id"
          class="chart-type-btn"
          :class="{ active: activeChartType === type.id }"
          @click="activeChartType = type.id"
        >
          {{ $t(type.label) }}
        </button>

      </div>


      <div v-if="activeChartType === 'status'" class="chart-card">

        <h4 class="chart-title">
          {{ $t('commitments.statusDistribution') }}
        </h4>

        <div class="chart-wrapper">
          <PieChart :chart-data="statusChartData" :options="pieChartOptions" />
        </div>

        <div class="chart-summary">

          <div
            v-for="(status,index) in statusDistribution"
            :key="index"
            class="summary-item"
          >
            <span class="summary-label">{{ $t(status.label) }}:</span>

            <span class="summary-value">
              {{ status.count }} {{ $t('commitments.commitments') }}
              ({{ status.percentage }}%)
            </span>

            <span class="summary-amount">
              {{ formatCurrency(status.totalAmount) }}
            </span>

          </div>

        </div>
      </div>


      <div v-if="activeChartType === 'top'" class="chart-card">
        <h4 class="chart-title">
          {{ $t('commitments.topCommitments') }}
        </h4>

        <div class="chart-wrapper">
          <BarChart :chart-data="topCommitmentsChartData" :options="barChartOptions"/>
        </div>
      </div>


      <div v-if="activeChartType === 'timeline'" class="chart-card">

        <h4 class="chart-title">
          {{ $t('commitments.timelineChart') }}
        </h4>

        <div class="chart-wrapper">
          <LineChart :chart-data="timelineChartData" :options="lineChartOptions"/>
        </div>

      </div>


      <div v-if="activeChartType === 'monthly'" class="chart-card">

        <h4 class="chart-title">
          {{ $t('commitments.monthlyDistribution') }}
        </h4>

        <div class="chart-wrapper">
          <BarChart :chart-data="monthlyChartData" :options="monthlyBarOptions"/>
        </div>

      </div>

    </div>
  </div>


  <!-- LEGEND -->

  <div class="date-legend">

    <div class="legend-title">
      {{ $t('commitments.dueDateLegend') }}
    </div>

    <div class="legend-items">

      <div class="legend-item overdue">
        <span class="legend-color"></span>
        <span class="legend-text">{{ $t('dueDate.overdue') }}</span>
      </div>

      <div class="legend-item today">
        <span class="legend-color"></span>
        <span class="legend-text">{{ $t('dueDate.today') }}</span>
      </div>

      <div class="legend-item this-week">
        <span class="legend-color"></span>
        <span class="legend-text">{{ $t('dueDate.thisWeek') }}</span>
      </div>

      <div class="legend-item this-month">
        <span class="legend-color"></span>
        <span class="legend-text">{{ $t('dueDate.thisMonth') }}</span>
      </div>

      <div class="legend-item future">
        <span class="legend-color"></span>
        <span class="legend-text">{{ $t('dueDate.future') }}</span>
      </div>

    </div>

  </div>


  <!-- ACTION BAR -->

  <div class="action-bar">

    <button class="print-btn" @click="printData">🖨️</button>

    <button class="refresh-btn" @click="fetchTaahodatData" :disabled="loading">
      🔄
      <span class="refresh-text">{{ $t('common.refresh') }}</span>
    </button>

    <button class="filter-btn" @click="toggleFilterPanel" :title="$t('filters.title')">
      🎛️
      <span class="filter-text">{{ $t('filters.filter') }}</span>

      <span v-if="activeFilterCount > 0" class="filter-badge">
        {{ activeFilterCount }}
      </span>
    </button>


    <div class="currency-selector">

      <label class="currency-label">
        {{ $t('currency.currency') }}
      </label>

      <select
        v-model="selectedCurrency"
        @change="onCurrencyChange"
        class="currency-select"
      >

        <option value="all">
          {{ $t('currency.allCurrencies') }}
        </option>

        <option
          v-for="arz in arzList"
          :key="arz.idd"
          :value="arz.idd"
        >
          {{ arz.nam }} ({{ arz.sign }})
        </option>

      </select>

      <div v-if="loadingArz" class="currency-loading">
        {{ $t('common.loading') }}
      </div>

    </div>


    <div class="search-container">

      <input
        type="text"
        v-model="searchQuery"
        :placeholder="$t('commitments.searchPlaceholder')"
        class="search-input"
      />

      <span class="search-icon">🔍</span>

    </div>

  </div>


  <!-- LOADING -->

  <div v-if="loading" class="loading-state">

    <div class="loading-spinner"></div>

    <span>
      {{ $t('commitments.loadingCommitments') }}
    </span>

  </div>


  <!-- EMPTY -->
  <div v-if="!loading && taahodatData.length === 0" class="empty-state">
    <span>{{ $t('commitments.noCommitments') }}</span>
  </div>

  <!-- Taahodat Data Section -->
  <div class="taahodat-section" v-if="filteredData.length > 0">
      <div class="taahodat-grid">
          <div 
              class="taahodat-card" 
              v-for="(record, index) in paginatedData" 
              :key="index"
              :class="getDueDateStatus(record.DateSaresid)"
          >
              <div class="card-header">
                  <div class="record-detail">
                    <span class="detail-label" style="width: 100%; display: flex; justify-content: space-between; align-items: center;">                
                        <span class="record-title">تعهد شماره {{ record.ChequeNum }}</span>
                        <span class="record-date" >سررسید: {{ formatPersianDate(record.DateSaresid) }}</span>
                    </span>
                  </div>              
              </div>
              <div class="card-body">
                  <div class="record-detail">
                      <div class="comment-section">
                          <span class="detail-value">{{ record.Chequebabat || 'بدون شرح' }}</span>
                      </div>
                      <div class="amount-section">
                          <span class="record-amount" style="font-family: 'Gill Sans', 'Gill Sans MT', Calibri, 'Trebuchet MS', sans-serif;">
                              {{ formatCurrency(record.ChequePrice) }} 
                              <span style="font-size: medium;">{{ record.namArz }}</span>
                          </span>
                          <span class="due-date-badge" :class="getDueDateStatus(record.DateSaresid) + '-badge'">
                              {{ getDueDateText(record.DateSaresid) }}
                          </span>
                      </div>
                  </div>

                  <div class="taahodat-details">
                      <div class="detail-row">
                          <span class="detail-label">مانده:</span>
                          <span class="detail-value">{{ formatCurrency(record.Mand) }} {{ record.namArz }}</span>
                        </div>
                      <!-- <div class="detail-row">
                          <span class="detail-label">عملکرد:</span>
                          <span class="detail-value">{{ record.Act || 'نامشخص' }}</span>
                      </div>
                      <div class="detail-row">
                          <span class="detail-label">شناسه چک:</span>
                          <span class="detail-value">{{ record.Cheque_ID || 'نامشخص' }}</span>
                      </div> -->
                  </div>

                  <button 
                      class="details-btn"
                      @click="openChequeDetails(record)"
                      :title="'مشاهده جزئیات تعهد'"
                  >
                      جزئیات تعهد
                  </button>
              </div>
          </div>
      </div>
      
      <!-- Pagination Controls Bottom -->
      <div class="pagination-controls">
          <button 
              @click="prevPage" 
              :disabled="currentPage === 1"
              class="pagination-btn"
          >
              قبلی
          </button>
          <span class="page-info">
              صفحه {{ currentPage }} از {{ totalPages }}
          </span>
          <button 
              @click="nextPage" 
              :disabled="currentPage === totalPages"
              class="pagination-btn"
          >
              بعدی
          </button>
      </div>
  </div>

  <!-- Cheque Details Modal -->
  <div v-if="showChequeModal" class="modal-overlay" @click="closeChequeModal">
      <div class="modal-content" @click.stop>
          <div class="modal-header">
              <h3>جزئیات</h3>
              <div class="modal-actions">
                  <button class="print-modal-btn" @click="printChequeDetails" title="چاپ جزئیات">
                      🖨️ چاپ
                  </button>
                  <button class="close-btn" @click="closeChequeModal">×</button>
              </div>
          </div>
          
          <div class="modal-body">
              <div class="cheque-details" v-if="selectedCheque">
                  <div class="detail-section">
                      <h4 class="section-title">اطلاعات اصلی</h4>
                      <div class="info-box">
                          <div class="info-row">
                              <span class="info-label">تعهد شماره:</span>
                              <span class="info-value">{{ selectedCheque.ChequeNum || 'نامشخص' }}</span>
                          </div>
                          <div class="info-row">
                              <span class="info-label">مبلغ:</span>
                              <span class="info-value amount">{{ formatCurrency(selectedCheque.ChequePrice) }} {{ selectedCheque.namArz }}</span>
                          </div>
                          <div class="info-row">
                              <span class="info-label">مانده:</span>
                              <span class="info-value amount">{{ formatCurrency(selectedCheque.Mand) }} {{ selectedCheque.namArz }}</span>
                          </div>
                          <div class="info-row">
                              <span class="info-label">تاریخ سررسید:</span>
                              <span class="info-value" :class="getDueDateStatus(selectedCheque.DateSaresid) + '-text'">
                                  {{ formatPersianDate(selectedCheque.DateSaresid) }}
                                  <span class="due-date-indicator">({{ getDueDateText(selectedCheque.DateSaresid) }})</span>
                              </span>
                          </div>
                      </div>
                  </div>

                  <!-- <div class="detail-section">
                      <h4 class="section-title">اطلاعات تکمیلی</h4>
                      <div class="info-box">
                          <div class="info-row">
                              <span class="info-label">عملکرد:</span>
                              <span class="info-value">{{ selectedCheque.Act || 'نامشخص' }}</span>
                          </div>
                          <div class="info-row">
                              <span class="info-label">شناسه چک:</span>
                              <span class="info-value">{{ selectedCheque.Cheque_ID || 'نامشخص' }}</span>
                          </div>
                          <div class="info-row">
                              <span class="info-label">شناسه داخلی:</span>
                              <span class="info-value">{{ selectedCheque.ID_Checks || 'نامشخص' }}</span>
                          </div>
                          <div class="info-row">
                              <span class="info-label">شرح:</span>
                              <span class="info-value">{{ selectedCheque.Chequebabat || 'بدون شرح' }}</span>
                          </div>
                      </div>
                  </div> -->
              </div>
          </div>

          <div class="modal-footer">
              <button class="close-modal-btn" @click="closeChequeModal">
                  بستن
              </button>
          </div>
      </div>
  </div>

  </div>
</template>

<script>
import { computed, onMounted, ref, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { accountingService } from '@/services/api'
import Navbar from '@/components/Navbar.vue'
import { useRouter } from 'vue-router'
import DatePicker from 'vue3-persian-datetime-picker'

import BarChart from '@/components/charts/BarChart.vue'
import LineChart from '@/components/charts/LineChart.vue'
import PieChart from '@/components/charts/PieChart.vue'

export default {
  name: 'TaahodatView',
  components: {
    Navbar,
    DatePicker,

    BarChart,
    LineChart,
    PieChart,
  },
  setup() {
    const authStore = useAuthStore()
    const taahodatData = ref([])
    const loading = ref(false)
    const error = ref(null)
    const searchQuery = ref('')
    const currentPage = ref(1)
    const itemsPerPage = 10
    const showChequeModal = ref(false)
    const selectedCheque = ref(null)
    const router = useRouter()

    const arzList = ref([])
    const selectedCurrency = ref(1)
    const loadingArz = ref(false)

    // Add filter states
    const showFilterPanel = ref(false)
    const filters = ref({
        currency: 'all',
        dateFrom: '',
        dateTo: '',
        amountFrom: '',
        amountTo: '',
        dueDateStatus: [] // ['overdue', 'today', 'this-week', 'this-month', 'future']
    })

    // Handle currency change from main selector
    const onCurrencyChange = () => {
        filters.value.currency = selectedCurrency.value
        currentPage.value = 1
    }

    // Get currency name for display
    const getCurrencyName = (currencyId) => {
        if (currencyId === 'all') return 'همه'
        const arz = arzList.value.find(a => a.idd == currencyId)
        return arz ? `${arz.nam} (${arz.sign})` : 'نامشخص'
    }

    // ============ CHART-RELATED VARIABLES ============
    const showCharts = ref(true)
    const activeChartType = ref('status')
    const chartCurrencyFilter = ref('all')
    
    const chartTypes = ref([
      { id: 'status', label: 'وضعیت سررسید' },
      { id: 'top', label: 'بزرگترین تعهدات' },
      { id: 'timeline', label: 'خطی زمانی' },
      { id: 'monthly', label: 'توزیع ماهانه' }
    ])

    // ============ CHART DATA COMPUTED PROPERTIES ============
    
    // Data filtered for charts
    const chartData = computed(() => {
      let data = taahodatData.value
      
      if (chartCurrencyFilter.value !== 'all') {
        data = data.filter(record => record.chequeArz == chartCurrencyFilter.value)
      }
      
      return data
    })

    // Status distribution chart data
    const statusDistribution = computed(() => {
      const statuses = {
        'overdue': { label: 'سررسید گذشته', count: 0, totalAmount: 0, color: '#ff4757' },
        'today': { label: 'امروز', count: 0, totalAmount: 0, color: '#ffa502' },
        'this-week': { label: 'این هفته', count: 0, totalAmount: 0, color: '#3742fa' },
        'this-month': { label: 'این ماه', count: 0, totalAmount: 0, color: '#2ed573' },
        'future': { label: 'آینده', count: 0, totalAmount: 0, color: '#70a1ff' }
      }

      chartData.value.forEach(record => {
        const status = getDueDateStatus(record.DateSaresid)
        if (statuses[status]) {
          statuses[status].count++
          statuses[status].totalAmount += record.ChequePrice || 0
        }
      })

      const total = chartData.value.length
      return Object.values(statuses).map(status => ({
        ...status,
        percentage: total > 0 ? Math.round((status.count / total) * 100) : 0
      })).filter(status => status.count > 0)
    })

    const statusChartData = computed(() => {
      const distribution = statusDistribution.value
      
      return {
        labels: distribution.map(s => s.label),
        datasets: [{
          data: distribution.map(s => s.totalAmount),
          backgroundColor: distribution.map(s => s.color),
          borderColor: '#fff',
          borderWidth: 2,
          hoverOffset: 15
        }]
      }
    })

    // Top commitments chart data
    const topCommitments = computed(() => {
      return [...chartData.value]
        .sort((a, b) => (b.ChequePrice || 0) - (a.ChequePrice || 0))
        .slice(0, 10)
    })

    const topCommitmentsChartData = computed(() => {
      const top = topCommitments.value
      
      return {
        labels: top.map(record => 
          `تع. ${record.ChequeNum || 'بدون شماره'} - ${record.Chequebabat?.slice(0, 20) || 'بدون شرح'}`
        ),
        datasets: [{
          label: 'مبلغ تعهد',
          data: top.map(record => record.ChequePrice || 0),
          backgroundColor: top.map((_, index) => 
            `hsl(${index * 36}, 70%, 60%)`
          ),
          borderColor: top.map((_, index) => 
            `hsl(${index * 36}, 70%, 40%)`
          ),
          borderWidth: 1
        }]
      }
    })

    // Timeline chart data
    const timelineChartData = computed(() => {
      // Group by Persian month
      const monthlyData = {}
      
      chartData.value.forEach(record => {
        if (record.DateSaresid) {
          const parts = record.DateSaresid.split('/')
          if (parts.length === 3) {
            const yearMonth = `${parts[0]}/${parts[1]}`
            if (!monthlyData[yearMonth]) {
              monthlyData[yearMonth] = {
                total: 0,
                count: 0
              }
            }
            monthlyData[yearMonth].total += record.ChequePrice || 0
            monthlyData[yearMonth].count++
          }
        }
      })

      const sortedMonths = Object.keys(monthlyData).sort()
      
      // Persian month names
      const persianMonths = [
        'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
        'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'
      ]

      return {
        labels: sortedMonths.map(monthKey => {
          const [year, month] = monthKey.split('/')
          return `${persianMonths[parseInt(month) - 1]} ${year}`
        }),
        datasets: [
          {
            label: 'مجموع مبالغ',
            data: sortedMonths.map(month => monthlyData[month].total),
            borderColor: '#ff6b81',
            backgroundColor: 'rgba(255, 107, 129, 0.1)',
            tension: 0.4,
            fill: true
          },
          {
            label: 'تعداد تعهدات',
            data: sortedMonths.map(month => monthlyData[month].count),
            borderColor: '#3742fa',
            backgroundColor: 'rgba(55, 66, 250, 0.1)',
            tension: 0.4,
            fill: true
          }
        ]
      }
    })

    // Monthly distribution chart data
    const monthlyChartData = computed(() => {
      const monthlyTotals = Array(12).fill(0)
      const monthlyCounts = Array(12).fill(0)
      
      chartData.value.forEach(record => {
        if (record.DateSaresid) {
          const parts = record.DateSaresid.split('/')
          if (parts.length === 3) {
            const monthIndex = parseInt(parts[1]) - 1
            if (monthIndex >= 0 && monthIndex < 12) {
              monthlyTotals[monthIndex] += record.ChequePrice || 0
              monthlyCounts[monthIndex]++
            }
          }
        }
      })

      // Persian month names
      const persianMonths = [
        'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
        'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'
      ]

      return {
        labels: persianMonths,
        datasets: [{
          label: 'مجموع مبالغ',
          data: monthlyTotals,
          backgroundColor: 'rgba(52, 152, 219, 0.7)',
          borderColor: '#2980b9',
          borderWidth: 1
        }]
      }
    })

    // Chart options
    const pieChartOptions = computed(() => ({
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            font: {
              size: 12,
              family: 'Vazir, Arial, sans-serif'
            },
            padding: 20
          }
        }
      }
    }))

    const barChartOptions = computed(() => ({
      indexAxis: 'y',
      scales: {
        x: {
          ticks: {
            callback: function(value) {
              return new Intl.NumberFormat('fa-IR').format(value)
            }
          }
        }
      }
    }))

    const lineChartOptions = computed(() => ({
      scales: {
        y: {
          ticks: {
            callback: function(value) {
              return new Intl.NumberFormat('fa-IR').format(value)
            }
          }
        }
      }
    }))

    const monthlyBarOptions = computed(() => ({
      indexAxis: 'x',
      scales: {
        y: {
          ticks: {
            callback: function(value) {
              return new Intl.NumberFormat('fa-IR').format(value)
            }
          }
        }
      }
    }))

    // ============ CHART FUNCTIONS ============
    const toggleCharts = () => {
      showCharts.value = !showCharts.value
    }

    const updateCharts = () => {
      // Charts will automatically update due to computed properties
    }

    const printCharts = () => {
      const printWindow = window.open('', '_blank')
      const chartsHTML = generatePrintableCharts()
      
      printWindow.document.write(`
        <html dir="rtl">
          <head>
            <title>چاپ نمودارهای تعهدات</title>
            <style>
              body { 
                font-family: Vazir, Tahoma, Arial, sans-serif; 
                margin: 20px;
                direction: rtl;
              }
              .print-header { 
                text-align: center; 
                color: #333;
                margin-bottom: 30px;
                padding-bottom: 15px;
                border-bottom: 2px solid #667eea;
              }
              .chart-container {
                margin: 20px 0;
                page-break-inside: avoid;
              }
              .chart-title {
                text-align: center;
                color: #2c3e50;
                margin-bottom: 15px;
              }
              .chart-summary {
                background: #f8f9fa;
                padding: 15px;
                border-radius: 8px;
                margin-top: 15px;
              }
              .summary-item {
                display: flex;
                justify-content: space-between;
                padding: 5px 0;
                border-bottom: 1px solid #e0e0e0;
              }
              .summary-item:last-child {
                border-bottom: none;
              }
              .print-info {
                text-align: center;
                margin: 20px 0;
                color: #666;
                font-size: 14px;
              }
              @media print {
                body { margin: 0; }
                .chart-container { 
                  width: 100% !important;
                  height: 400px !important;
                }
              }
            </style>
          </head>
          <body>
            <div class="print-header">
              <h1>گزارش نموداری تعهدات</h1>
              <div>تاریخ چاپ: ${new Date().toLocaleDateString('fa-IR')}</div>
              <div>تعداد کل تعهدات: ${chartData.value.length}</div>
              <div>مجموع مبالغ: ${formatCurrency(chartData.value.reduce((sum, r) => sum + (r.ChequePrice || 0), 0))}</div>
            </div>
            ${chartsHTML}
            <div class="print-info">
              <p>این گزارش به صورت خودکار توسط سیستم تولید شده است</p>
            </div>
          </body>
        </html>
      `)
      
      printWindow.document.close()
      printWindow.focus()
      setTimeout(() => {
        printWindow.print()
      }, 1000) // Wait for charts to render
    }

    const generatePrintableCharts = () => {
      // For printing, we'll show all chart types
      return `
        <div class="chart-container">
          <h3 class="chart-title">توزیع تعهدات بر اساس وضعیت سررسید</h3>
          <div style="height: 400px;">
            <img src="${generateChartImage(statusChartData.value, 'pie')}" style="width: 100%; height: 100%; object-fit: contain;">
          </div>
          <div class="chart-summary">
            ${statusDistribution.value.map(status => `
              <div class="summary-item">
                <span>${status.label}</span>
                <span>${status.count} تعهد (${status.percentage}%)</span>
                <span>${formatCurrency(status.totalAmount)}</span>
              </div>
            `).join('')}
          </div>
        </div>

        <div class="chart-container">
          <h3 class="chart-title">بزرگترین تعهدات</h3>
          <div style="height: 400px;">
            <img src="${generateChartImage(topCommitmentsChartData.value, 'bar')}" style="width: 100%; height: 100%; object-fit: contain;">
          </div>
        </div>
      `
    }

    const generateChartImage = () => {
      // In a real implementation, you would use Chart.js toDataURL() method
      // or a server-side rendering solution
      return '' // Placeholder - implement chart image generation
    }

    // Add chartData to watch
    watch(chartData, () => {
      // Charts will auto-update due to computed properties
    }, { deep: true })

    // Filter functions
    const toggleFilterPanel = () => {
      showFilterPanel.value = !showFilterPanel.value
    }

    const closeFilterPanel = () => {
      showFilterPanel.value = false
    }

    const applyFilters = () => {
      currentPage.value = 1
      closeFilterPanel()
    }

    const clearFilters = () => {
        filters.value = {
            currency: 'all',
            dateFrom: '',
            dateTo: '',
            amountFrom: '',
            amountTo: '',
            dueDateStatus: []
        }
        selectedCurrency.value = 'all'
        currentPage.value = 1
    }

    const activeFilterCount = computed(() => {
        let count = 0
        if (filters.value.currency && filters.value.currency !== 'all') count++
        if (filters.value.dateFrom) count++
        if (filters.value.dateTo) count++
        if (filters.value.amountFrom) count++
        if (filters.value.amountTo) count++
        if (filters.value.dueDateStatus.length > 0) count++
        return count
    })

    const getDueDateStatusText = () => {
      if (filters.value.dueDateStatus.length === 5) return 'همه'
      return filters.value.dueDateStatus.map(status => {
        const texts = {
          'overdue': 'سررسید گذشته',
          'today': 'امروز',
          'this-week': 'این هفته',
          'this-month': 'این ماه',
          'future': 'آینده'
        }
        return texts[status] || status
      }).join('، ')
    }

    const formatFilterDate = (dateString) => {
      if (!dateString) return ''
      
      try {
        // The date from DatePicker is already in Persian format: jYYYY/jMM/jDD
        const parts = dateString.split('/')
        if (parts.length !== 3) return dateString
        
        const year = parts[0]
        const month = parseInt(parts[1])
        const day = parts[2]
        
        // Persian month names
        const persianMonths = [
          'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
          'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'
        ]
        
        if (month >= 1 && month <= 12) {
          return `${day} ${persianMonths[month - 1]} ${year}`
        }
        
        return dateString
      } catch {
        return dateString
      }
    }

    const persianToGregorian = (persianDate) => {
      if (!persianDate) return null
      try {
        const parts = persianDate.split('/')
        if (parts.length !== 3) return null
        
        const pYear = parseInt(parts[0])
        const pMonth = parseInt(parts[1])
        const pDay = parseInt(parts[2])
        
        const persianEpoch = new Date(622, 2, 21) // March 21, 622 AD (start of Persian calendar)
        const persianDays = (pYear - 1) * 365 + 
                          Math.floor((pYear * 8 + 21) / 33) + // Leap years calculation
                          (pMonth <= 6 ? (pMonth - 1) * 31 : 186 + (pMonth - 7) * 30) +
                          pDay - 1
        
        const gDate = new Date(persianEpoch)
        gDate.setDate(persianEpoch.getDate() + persianDays)
        
        return gDate
      } catch (error) {
        console.error('Error converting Persian date:', persianDate, error)
        return null
      }
    }

    const getDueDateStatus = (persianDate) => {
      if (!persianDate) return 'future'
      
      try {
        // Extract Persian year and month from the Persian date string
        const parts = persianDate.split('/')
        if (parts.length !== 3) return 'future'
        
        const duePersianYear = parseInt(parts[0])
        const duePersianMonth = parseInt(parts[1])
        const duePersianDay = parseInt(parts[2])
        
        // Get current Persian date - we need to handle Persian numerals
        const today = new Date()
        
        // Method 1: Convert Persian numerals to Western numerals
        const todayPersianDate = today.toLocaleDateString('fa-IR') // Format: ۱۴۰۴/۱۰/۱۵
        const todayParts = todayPersianDate.split('/')
        
        // Convert Persian numerals to Western numerals
        const persianToWestern = (persianNum) => {
          const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹']
          let westernNum = ''
          for (let char of persianNum) {
            const index = persianDigits.indexOf(char)
            if (index !== -1) {
              westernNum += index
            } else {
              westernNum += char
            }
          }
          return westernNum
        }
        
        const currentPersianYear = parseInt(persianToWestern(todayParts[0]))
        const currentPersianMonth = parseInt(persianToWestern(todayParts[1]))
        const currentPersianDay = parseInt(persianToWestern(todayParts[2]))
        
        console.log(`Today Persian: ${currentPersianYear}/${currentPersianMonth}/${currentPersianDay}, Due Persian: ${duePersianYear}/${duePersianMonth}/${duePersianDay}`)
        
        // Convert to Gregorian for day difference calculation
        const dueGregorian = persianToGregorian(persianDate)
        
        if (!dueGregorian) return 'future'
        
        // Normalize dates
        const todayNormalized = new Date(today.getFullYear(), today.getMonth(), today.getDate())
        const dueDateNormalized = new Date(dueGregorian.getFullYear(), dueGregorian.getMonth(), dueGregorian.getDate())
        
        const timeDiff = dueDateNormalized.getTime() - todayNormalized.getTime()
        const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24))
        
        console.log(`Days Diff: ${daysDiff}`)
        
        if (daysDiff < 0) return 'overdue'
        if (daysDiff === 0) return 'today'
        if (daysDiff <= 7) return 'this-week'
        
        // Check if dates are in the same PERSIAN month AND year
        if (currentPersianYear === duePersianYear && currentPersianMonth === duePersianMonth) {
          return 'this-month'
        }
        
        return 'future'
        
      } catch (error) {
        console.error('Error calculating due date status:', error)
        return 'future'
      }
    }

    const getDueDateText = (persianDate) => {
      const status = getDueDateStatus(persianDate)
      const texts = {
        'overdue': 'سررسید گذشته',
        'today': 'امروز',
        'this-week': 'این هفته',
        'this-month': 'این ماه',
        'future': 'آینده'
      }
      return texts[status] || 'آینده'
    }

    // Enhanced filter function with currency filter
    const filteredData = computed(() => {
        let filtered = taahodatData.value

        // Apply currency filter
        if (filters.value.currency && filters.value.currency !== 'all') {
            filtered = filtered.filter(record => {
                return record.chequeArz == filters.value.currency
            })
        }

        // Apply search filter
        if (searchQuery.value.trim()) {
            const query = searchQuery.value.trim().toLowerCase()
            filtered = filtered.filter(record => {
                const chequeNum = (record.ChequeNum || '').toString().toLowerCase()
                const description = (record.Chequebabat || '').toLowerCase()
                const amount = (record.ChequePrice || 0).toString()
                
                return chequeNum.includes(query) || description.includes(query) || amount.includes(query)
            })
        }

        // Apply date range filter
        if (filters.value.dateFrom) {
            const fromDate = persianToGregorian(filters.value.dateFrom)
            if (fromDate) {
                fromDate.setHours(0, 0, 0, 0)
                filtered = filtered.filter(record => {
                    if (!record.DateSaresid) return false
                    const recordDate = persianToGregorian(record.DateSaresid)
                    return recordDate && recordDate >= fromDate
                })
            }
        }

        if (filters.value.dateTo) {
            const toDate = persianToGregorian(filters.value.dateTo)
            if (toDate) {
                toDate.setHours(23, 59, 59, 999)
                filtered = filtered.filter(record => {
                    if (!record.DateSaresid) return false
                    const recordDate = persianToGregorian(record.DateSaresid)
                    return recordDate && recordDate <= toDate
                })
            }
        }

        // Apply amount range filter
        if (filters.value.amountFrom) {
            const minAmount = parseFloat(filters.value.amountFrom)
            filtered = filtered.filter(record => {
                const amount = record.ChequePrice || 0
                return amount >= minAmount
            })
        }

        if (filters.value.amountTo) {
            const maxAmount = parseFloat(filters.value.amountTo)
            filtered = filtered.filter(record => {
                const amount = record.ChequePrice || 0
                return amount <= maxAmount
            })
        }

        // Apply due date status filter
        if (filters.value.dueDateStatus.length > 0) {
            filtered = filtered.filter(record => {
                const status = getDueDateStatus(record.DateSaresid)
                return filters.value.dueDateStatus.includes(status)
            })
        }

        return filtered
    })

    // Fetch all ARZ (currencies)
    const fetchAllArz = async () => {
      loadingArz.value = true
      try {
        console.log('🔧 Fetching all ARZ...')
        const response = await accountingService.getAllArz()
        if (response.data.success) {
          arzList.value = response.data.data
          console.log('✅ ARZ list loaded:', arzList.value)
        } else {
          console.error('❌ Failed to load ARZ:', response.data.message)
        }
      } catch (err) {
        console.error('❌ Error fetching ARZ:', err)
      } finally {
        loadingArz.value = false
      }
    }

    // Get currency sign for display
    const getCurrencySign = () => {
      if (selectedCurrency.value === 1) return 'ریال'
      const selectedArz = arzList.value.find(arz => arz.idd === selectedCurrency.value)
      return selectedArz ? selectedArz.sign : 'ریال'
    }

    const getTotalCurrencySign = () => {
      if (taahodatData.value.length === 0) return 'ریال'
      // Use the currency from the first record as representative for the total
      return taahodatData.value[0]?.namArz || 'ریال'
    }

    // Calculate total taahodat
    const formatTotalTaahodat = computed(() => {
      const total = taahodatData.value.reduce((sum, record) => sum + (record.ChequePrice || 0), 0)
      return formatCurrency(total)
    })

    // Paginated data
    const paginatedData = computed(() => {
      const startIndex = (currentPage.value - 1) * itemsPerPage
      const endIndex = startIndex + itemsPerPage
      return filteredData.value.slice(startIndex, endIndex)
    })

    // Total pages
    const totalPages = computed(() => {
      return Math.ceil(filteredData.value.length / itemsPerPage)
    })

    // Pagination methods
    const nextPage = () => {
      if (currentPage.value < totalPages.value) {
        currentPage.value++
      }
    }

    const prevPage = () => {
      if (currentPage.value > 1) {
        currentPage.value--
      }
    }

    // Reset to first page when search changes
    watch(searchQuery, () => {
      currentPage.value = 1
    })

    // Fetch taahodat data
    const fetchTaahodatData = async () => {
      if (!authStore.user?.customerId) {
        console.error('No customer ID found')
        return
      }

      loading.value = true
      error.value = null

      try {
        console.log('Fetching taahodat data for customer ID:', authStore.user.subCodeId)
        
        // You might need to adjust the AC parameter based on your business logic
        const acValue = 4 // Default AC value, adjust as needed
        
        const response = await accountingService.getTaahodatY(acValue)
        if (response.data.success) {
          // Sort the data by date (ascending - oldest first)
          const sortedData = (response.data.data || []).sort((a, b) => {
            if (!a.DateSaresid) return 1
            if (!b.DateSaresid) return -1
            
            // Parse Persian dates for comparison
            const dateA = persianToGregorian(a.DateSaresid)
            const dateB = persianToGregorian(b.DateSaresid)
            
            if (!dateA) return 1
            if (!dateB) return -1
            
            return dateA.getTime() - dateB.getTime() // Ascending order
          })
          
          taahodatData.value = sortedData
          console.log('Taahodat data fetched and sorted:', taahodatData.value.length, 'records')
        } else {
          error.value = response.data.message || 'خطا در دریافت اطلاعات تعهدات'
        }

      } catch (err) {
        console.error('Error fetching taahodat data:', err)
        error.value = err.response?.data?.detail || 'خطا در دریافت اطلاعات'
      } finally {
        loading.value = false
      }
    }

    const formatCurrency = (amount) => {
      if (!amount && amount !== 0) return '۰'
      return new Intl.NumberFormat('fa-IR').format(amount)
    }

    const formatPersianDate = (dateString) => {
      if (!dateString) return 'تاریخ نامعلوم'
      
      try {
        // Split the date string (format: 1404/06/18)
        const parts = dateString.split('/')
        if (parts.length !== 3) return dateString
        
        const year = parts[0]
        const month = parseInt(parts[1])
        const day = parts[2]
        
        // Persian month names
        const persianMonths = [
          'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
          'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'
        ]
        
        if (month >= 1 && month <= 12) {
          return `${day} ${persianMonths[month - 1]} ${year}`
        }
        
        return dateString
      } catch {
        return dateString
      }
    }

    const navigateToDashboard = () => {
      router.push('/dashboard')
    }

    const openChequeDetails = (cheque) => {
      selectedCheque.value = cheque
      showChequeModal.value = true
    }

    const closeChequeModal = () => {
      showChequeModal.value = false
      selectedCheque.value = null
    }

    const printData = () => {
      const printWindow = window.open('', '_blank')
      const tableContent = generatePrintableTable()
      
      printWindow.document.write(`
        <html dir="rtl">
          <head>
            <title>چاپ اطلاعات تعهدات</title>
            <style>
              body { 
                font-family: Tahoma, Arial, sans-serif; 
                margin: 20px;
                direction: rtl;
              }
              h1 { 
                text-align: center; 
                color: #333;
                margin-bottom: 20px;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                margin-top: 10px;
              }
              th, td {
                border: 1px solid #ddd;
                padding: 8px;
                text-align: right;
              }
              th {
                background-color: #f5f5f5;
                font-weight: bold;
              }
              .amount { 
                font-weight: bold;
                color: #2d3436;
              }
              @media print {
                body { margin: 0; }
                table { width: 100%; }
              }
              .print-button {
                text-align: center;
                margin-top: 20px;
                padding: 20px;
              }
              @media print {
                .print-button { display: none; }
              }
            </style>
          </head>
          <body>
            <h1>گزارش تعهدات شما</h1>
            <div>تاریخ چاپ: ${new Date().toLocaleDateString('fa-IR')}</div>
            <div>تعداد رکوردها: ${filteredData.value.length}</div>
            <div>مجموع مبالغ: ${formatTotalTaahodat.value} ${getTotalCurrencySign()}</div>
            ${tableContent}
            <div class="print-button">
              <button onclick="window.print()" style="padding: 10px 20px; margin: 5px;">
                چاپ
              </button>
              <button onclick="window.close()" style="padding: 10px 20px; margin: 5px;">
                بستن
              </button>
            </div>
          </body>
        </html>
      `)
      
      printWindow.document.close()
      printWindow.focus()
      setTimeout(() => {
        printWindow.print()
      }, 250)
    }

    const generatePrintableTable = () => {
      return `
        <table>
          <thead>
            <tr>
              <th>شماره تعهد</th>
              <th>شرح</th>
              <th>مبلغ</th>
              <th>مانده</th>
              <th>تاریخ سررسید</th>
              <th>عملکرد</th>
              <th>وضعیت</th>
            </tr>
          </thead>
          <tbody>
            ${filteredData.value.map(record => `
              <tr>
                <td>${record.ChequeNum || '-'}</td>
                <td>${record.Chequebabat || 'بدون شرح'}</td>
                <td class="amount">${formatCurrency(record.ChequePrice)} ${record.namArz}</td>
                <td class="amount">${formatCurrency(record.Mand)} ${record.namArz}</td>
                <td>${formatPersianDate(record.DateSaresid)}</td>
                <td>${record.Act || '-'}</td>
                <td>${getDueDateText(record.DateSaresid)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      `
    }

    const printChequeDetails = () => {
      if (!selectedCheque.value) return
      
      const printWindow = window.open('', '_blank')
      const cheque = selectedCheque.value
      
      printWindow.document.write(`
        <html dir="rtl">
          <head>
            <title>چاپ جزئیات تعهد - شماره ${cheque.ChequeNum}</title>
            <style>
              body { 
                font-family: Tahoma, Arial, sans-serif; 
                margin: 20px;
                direction: rtl;
                line-height: 1.6;
              }
              .print-header { 
                text-align: center; 
                color: #333;
                margin-bottom: 30px;
                padding-bottom: 15px;
                border-bottom: 2px solid #667eea;
              }
              .detail-section {
                margin-bottom: 20px;
              }
              .section-title {
                color: #333;
                border-bottom: 1px solid #ddd;
                padding-bottom: 5px;
              }
              .info-box {
                background: #f8f9fa;
                padding: 15px;
                border-radius: 8px;
                margin-top: 10px;
              }
              .info-row {
                display: flex;
                justify-content: space-between;
                padding: 8px 0;
                border-bottom: 1px solid #e0e0e0;
              }
              .info-row:last-child {
                border-bottom: none;
              }
              .info-label {
                font-weight: bold;
                color: #555;
              }
              .info-value {
                color: #333;
              }
              .amount {
                font-weight: bold;
                color: #2d3436;
              }
              @media print {
                body { margin: 0; }
              }
            </style>
          </head>
          <body>
            <div class="print-header">
              <h1>جزئیات تعهد شماره ${cheque.ChequeNum}</h1>
              <div>تاریخ چاپ: ${new Date().toLocaleDateString('fa-IR')}</div>
            </div>
            
            <div class="detail-section">
              <h3 class="section-title">اطلاعات اصلی </h3>
              <div class="info-box">
                <div class="info-row">
                  <span class="info-label">شماره :</span>
                  <span class="info-value">${cheque.ChequeNum || 'نامشخص'}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">مبلغ :</span>
                  <span class="info-value amount">${formatCurrency(cheque.ChequePrice)} ${cheque.namArz}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">مانده:</span>
                  <span class="info-value amount">${formatCurrency(cheque.Mand)} ${cheque.namArz}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">تاریخ سررسید:</span>
                  <span class="info-value">
                    ${formatPersianDate(cheque.DateSaresid)}
                    <span style="color: #666; font-size: 0.9em;">(${getDueDateText(cheque.DateSaresid)})</span>
                  </span>
                </div>
              </div>
            </div>

            <div class="detail-section">
              <h3 class="section-title">اطلاعات تکمیلی</h3>
              <div class="info-box">
                <div class="info-row">
                  <span class="info-label">عملکرد:</span>
                  <span class="info-value">${cheque.Act || 'نامشخص'}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">شناسه:</span>
                  <span class="info-value">${cheque.Cheque_ID || 'نامشخص'}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">شناسه داخلی:</span>
                  <span class="info-value">${cheque.ID_Checks || 'نامشخص'}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">شرح:</span>
                  <span class="info-value">${cheque.Chequebabat || 'بدون شرح'}</span>
                </div>
              </div>
            </div>
          </body>
        </html>
      `)
      
      printWindow.document.close()
      printWindow.focus()
      setTimeout(() => {
        printWindow.print()
      }, 250)
    }

    onMounted(async () => {
      if (authStore.isAuthenticated) {
        console.log("User is authenticated, fetching taahodat data...")
        await fetchAllArz()
        await fetchTaahodatData()
      }
    })

    return {
      taahodatData,
      filteredData,
      paginatedData,
      loading,
      error,
      searchQuery,
      currentPage,
      totalPages,
      showChequeModal,
      selectedCheque,
      arzList,
      loadingArz,
      formatTotalTaahodat,
      getCurrencySign,
      getTotalCurrencySign,
      formatCurrency,
      formatPersianDate,
      getDueDateStatus,
      getDueDateText,
      nextPage,
      prevPage,
      printData,
      openChequeDetails,
      closeChequeModal,
      printChequeDetails,
      navigateToDashboard,
      fetchTaahodatData,

      showFilterPanel,
      filters,
      activeFilterCount,
      selectedCurrency,
      onCurrencyChange,
      getCurrencyName,
      toggleFilterPanel,
      closeFilterPanel,
      applyFilters,
      clearFilters,
      getDueDateStatusText,
      formatFilterDate,

      // Chart-related
      showCharts,
      activeChartType,
      chartTypes,
      chartCurrencyFilter,
      statusChartData,
      topCommitmentsChartData,
      timelineChartData,
      monthlyChartData,
      statusDistribution,
      pieChartOptions,
      barChartOptions,
      lineChartOptions,
      monthlyBarOptions,
      toggleCharts,
      updateCharts,
      printCharts
    }
  }
}
</script>

<style scoped>
/* Taahodat specific styles */
.taahodat-section {
  padding: 15px;
}

.taahodat-grid {
  display: grid;
  gap: 15px;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
}

.taahodat-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.taahodat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
}

.record-title {
  font-weight: 700;
  color: #2d3436;
  font-size: 1.1rem;
}

.cheque-badge {
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%);
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
}

.taahodat-details {
  margin-top: 15px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #e9ecef;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-row .detail-label {
  font-weight: 600;
  color: #555;
  font-size: 0.9rem;
}

.detail-row .detail-value {
  color: #333;
  font-size: 0.9rem;
}

/* Responsive design */
@media (max-width: 768px) {
  .taahodat-grid {
    grid-template-columns: 1fr;
  }
  
  .taahodat-card {
    padding: 15px;
  }
  
  .action-bar {
    flex-direction: column;
    gap: 10px;
  }
  
  .search-container {
    width: 100%;
  }
}

/* Refresh button styles */
.refresh-btn {
  background: linear-gradient(135deg, #00b894 0%, #00a085 100%);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.3s ease;
}

.refresh-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 184, 148, 0.3);
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.refresh-text {
  font-size: 0.8rem;
}

/* Modal styles for cheque details */
.cheque-details {
  padding: 10px 0;
}

.cheque-details .detail-section {
  margin-bottom: 25px;
}

.cheque-details .section-title {
  font-size: 1.1rem;
  color: #333;
  margin-bottom: 15px;
  padding-bottom: 8px;
  border-bottom: 2px solid #667eea;
}
.bank-app {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: var(--bg);
  direction: rtl;
}

.account-type {
  font-size: 16px;
  margin-bottom: 8px;
  margin-left: 5px;
  opacity: 0.9;
}

.account-balance {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.balance-label {
  font-size: 14px;
  opacity: 0.8;
}

.balance-amount {
  font-size: 18px;
  font-weight: bold;
  margin-left: auto;
  margin-right: auto;
}
.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid #667eea;
}

.section-title {
  font-size: 1.1rem;
  color: #333;
  margin: 0;
}

.back-arrow {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 5px 10px;
  border-radius: 4px;
  transition: background-color 0.3s ease;
}

.back-arrow:hover {
  background-color: #f0f0f0;
}
.action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 0 10px;
}

.print-btn {
  background: #2c3e50;
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
}

.print-btn:hover {
  background: #34495e;
}

.search-container {
  position: relative;
  width: 300px;
}

.search-input {
  width: 100%;
  padding: 10px 40px 10px 15px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  direction: rtl;
}

.search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
}

.accounting-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.3rem;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: var(--opposite-bg);
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.modal-footer {
  padding: 15px 20px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
}

/* Loading State */
.modal-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #666;
}

.modal-loading .loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error State */
.modal-error {
  text-align: center;
  padding: 40px;
  color: #d63031;
}

.error-icon {
  font-size: 48px;
  margin-bottom: 15px;
}

.modal-error h4 {
  margin: 0 0 10px 0;
  color: #d63031;
}

.modal-error p {
  margin: 0 0 20px 0;
  color: #666;
}

.retry-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: transform 0.2s;
}

.retry-btn:hover {
  transform: translateY(-2px);
}

/* Empty State */
.modal-empty {
  text-align: center;
  padding: 40px;
  color: #666;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 15px;
}

.modal-empty h4 {
  margin: 0 0 10px 0;
  color: #333;
}

/* Commercial Details Styles */
.commercial-details {
  margin-top: 10px;
}

.details-grid {
  display: grid;
  gap: 20px;
}

.detail-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.detail-section {
  margin-bottom: 20px;
}

.detail-section:last-child {
  margin-bottom: 0;
}

.info-box {
  background: white;
  border-radius: 6px;
  padding: 5px;
  /*border: 1px solid #e0e0e0;*/
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  width: 100%;
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  font-weight: 600;
  color: #555;
  font-size: 0.9rem;
}

.info-value {
  color: #333;
  font-size: 0.9rem;
}

        /* Multi-values styling */
        .multi-values {
            display: flex;
            flex-direction: column;
            gap: 5px;
            flex: 1;
        }
        
        .multi-value-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 5px 0;
        }
        
        .multi-value-code {
            font-weight: 600;
            color: #4b6cb7;
            margin-left: 10px;
        }
        
        .multi-value-amount {
            color: #2c3e50;
            font-weight: 500;
        }

.info-value.amount {
  font-weight: 600;
  color: #2d3436;
}

.info-value.amount.total {
  color: #e74c3c;
  font-size: 1rem;
}

/* Details Button */
.details-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  margin-top: 10px;
  transition: all 0.3s ease;
  width: 100%;
}

.details-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3);
}

.details-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.close-modal-btn {
  background: #6c757d;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.close-modal-btn:hover {
  background: #5a6268;
}

/* Responsive Design */
@media (max-width: 768px) {
  .modal-content {
    width: 95%;
    margin: 10px;
  }
  
  .modal-header {
    padding: 15px;
  }
  
  .modal-body {
    padding: 15px;
  }
  
  .detail-card {
    padding: 15px;
  }
  
  .info-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }
  
  .info-label, .info-value {
    font-size: 0.85rem;
  }
}
.modal-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.print-modal-btn {
  background: linear-gradient(135deg, #27ae60 0%, #2ecc71 100%);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 5px;
}

.print-modal-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(39, 174, 96, 0.3);
}

/* Update modal header to accommodate the print button */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

/* Ensure the header content is properly aligned */
.modal-header h3 {
  margin: 0;
  font-size: 1.3rem;
  flex: 1;
}

/* Responsive adjustments for mobile */
@media (max-width: 768px) {
  .modal-actions {
    gap: 8px;
  }
  
  .print-modal-btn {
    padding: 6px 12px;
    font-size: 0.8rem;
  }
  
  .modal-header {
    padding: 15px;
  }
}
.accounting-section {
  margin-bottom: 24px;
}

.accounting-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.accounting-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #f0f0f0;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.accounting-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  /*padding-bottom: 12px;
   border-bottom: 1px solid #f0f0f0; */
}

.record-date {
  font-size: 12px;
  color: #666;
  font-weight: 500;
}

.record-amount {
  font-size: 14px;
  font-weight: bold;
}

.record-amount.debit {
  color: #e74c3c; /* Red for debit */
}

.record-amount.credit {
  color: #27ae60; /* Green for credit */
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.record-detail {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
}

.detail-label {
  font-size: 11px;
  color: #888;
  font-weight: 500;
  min-width: 60px;
}

.detail-value {
  font-size: 12px;
  color: #333;
  text-align: left;
  flex: 1;
}

.type-badge {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: bold;
  text-align: center;
}

.debit-badge {
  background-color: #ffeaea;
  color: #e74c3c;
}

.credit-badge {
  background-color: #e8f7ef;
  color: #27ae60;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #666;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #4a6baf;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #888;
  background: white;
  border-radius: 12px;
  border: 1px dashed #ddd;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .accounting-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .accounting-card {
    padding: 12px;
  }
  
  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .record-amount {
    align-self: flex-end;
  }
}

@media (min-width: 769px) and (max-width: 1024px) {
  .accounting-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1025px) {
  .accounting-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}



.debit-badge {
  background-color: #e74c3c; /* Red for بد */
}

.credit-badge {
  background-color: #27ae60; /* Green for بس */
}

.record-amount {
  font-weight: bold;
  font-size: 1.1rem;
}

.debit {
  color: #e74c3c;
}

.credit {
  color: #27ae60;
}

.record-detail {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
}

.comment-section {
  flex: 1;
  min-width: 0; /* Important for text truncation */
  text-align: right; /* For RTL languages */
}

.amount-section {
  display: flex;
  align-items: center;
  gap: 1px;
  flex-shrink: 0;
}

.detail-value {
  font-size: 12px;
  color: #333;
  text-align: right; /* For RTL */
  word-wrap: break-word;
  display: block;
}

.record-amount {
  font-weight: bold;
  font-size: 1.1rem;
}

.transaction-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 27px;
  height: 27px;
  border-radius: 50%;
  font-size: 0.8rem;
  font-weight: bold;
  color: white;
}
.sort-btn {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
  font-size: 14px;
}

.sort-btn:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

.sort-text {
  font-size: 12px;
  color: #495057;
}

.action-bar {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 20px;
}
.action-bar {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

/* Filter Button */
.filter-btn {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
  font-size: 14px;
  position: relative;
}

.filter-btn:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

.filter-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  background: #e74c3c;
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Filter Panel */
.filter-panel {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 0;
  margin-bottom: 20px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #dee2e6;
  background: #f8f9fa;
  border-radius: 8px 8px 0 0;
}

.filter-header h4 {
  margin: 0;
  color: #495057;
}

.close-filter-btn {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #6c757d;
}

.filter-content {
  padding: 20px;
}

.filter-group {
  margin-bottom: 20px;
}

.filter-label {
  display: block;
  margin-bottom: 10px;
  font-weight: 600;
  color: #495057;
}

.filter-options {
  display: flex;
  gap: 15px;
}

.filter-option {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.filter-option input[type="checkbox"] {
  margin: 0;
}

.date-inputs, .amount-inputs {
  display: flex;
  gap: 15px;
}

.date-input, .amount-input {
  flex: 1;
}

.date-input label, .amount-input label {
  display: block;
  margin-bottom: 5px;
  font-size: 14px;
  color: #6c757d;
}

.date-picker, .amount-picker {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 14px;
}

.filter-actions {
  display: flex;
  gap: 10px;
  margin-top: 20px;
}

.apply-filters-btn, .clear-filters-btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.apply-filters-btn {
  background: #007bff;
  color: white;
}

.clear-filters-btn {
  background: #6c757d;
  color: white;
}

/* Active Filters */
.active-filters {
  background: #e3f2fd;
  border: 1px solid #bbdefb;
  border-radius: 6px;
  padding: 15px;
  margin-bottom: 20px;
}

.active-filters-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.clear-all-filters {
  background: none;
  border: none;
  color: #e74c3c;
  cursor: pointer;
  font-size: 12px;
}

.active-filter-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.filter-tag {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 16px;
  padding: 6px 12px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.filter-tag button {
  background: none;
  border: none;
  cursor: pointer;
  color: #6c757d;
  font-size: 14px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .search-container{
    width: 100%;
  }
  .action-bar {
    flex-direction: row;
    align-items: center;
  }
  
  .date-inputs, .amount-inputs {
    flex-direction: column;
    gap: 10px;
  }
  
  .filter-options {
    flex-direction: column;
    gap: 10px;
  }
  
  .active-filter-tags {
    flex-direction: column;
  }
}
.currency-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: 15px;
}

.currency-label {
  font-size: 0.9rem;
  color: var(--text);
  white-space: nowrap;
}

.currency-select {
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: var(--card-bg);
  color: var(--text);
  font-size: 0.9rem;
  min-width: 120px;
}

.currency-select:disabled {
  background-color: #f5f5f5;
  opacity: 0.7;
}

.currency-loading {
  font-size: 0.8rem;
  color: #666;
  white-space: nowrap;
}

/* Responsive design for currency selector */
@media (max-width: 768px) {
  .currency-selector {
    margin-right: 10px;
  }
  
  .currency-select {
    min-width: 100px;
    font-size: 0.8rem;
  }
  
  .currency-label {
    font-size: 0.8rem;
  }
}

/* Update the action-bar to accommodate the new currency selector */
.action-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 10px;
  margin-bottom: 15px;
}

/* Ensure proper spacing in mobile view */
@media (max-width: 768px) {
  .action-bar {
    gap: 8px;
    padding: 10px;
    flex-direction: row;
  }
}

.info-row {
  display: flex;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
  width: 100%;
}

.info-label {
  font-weight: 600;
  color: #555;
  font-size: 0.9rem;
  width: 120px; /* Fixed width for perfect alignment */
  text-align: right;
  flex-shrink: 0;
  margin-left: 10px;
}

.info-value {
  color: #333;
  font-size: 0.9rem;
  text-align: right;
  flex: 1;
  padding-right: 15px;
}

/* Mobile styles */
@media (max-width: 768px) {
    .info-row {
        flex-direction: row;
        align-items: center;
        padding: 10px 0;
    }
    
    .info-label {
        width: 100px;
        text-align: right;
        margin-left: 5px;
        margin-bottom: 0;
        font-size: 0.85rem;
    }
    
    .info-value {
        width: auto;
        text-align: right;
        padding-right: 5px;
        font-size: 0.85rem;
        font-weight: 500;
        flex: 1;
    }
}
/* Date Legend */
.date-legend {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 10px;
  margin: 15px;
  border: 1px solid #e9ecef;
}

.legend-title {
  font-weight: 600;
  color: var(--text);
  margin-bottom: 10px;
  font-size: 0.9rem;
}

.legend-items {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.legend-text {
  font-size: 0.8rem;
  color: #555;
}

/* Color coding for due dates */
.overdue .legend-color { background: linear-gradient(135deg, #ff6b6b 0%, #c23616 100%); }
.today .legend-color { background: linear-gradient(135deg, #ff9ff3 0%, #f368e0 100%); }
.this-week .legend-color { background: linear-gradient(135deg, #feca57 0%, #ff9f43 100%); }
.this-month .legend-color { background: linear-gradient(135deg, #48dbfb 0%, #0abde3 100%); }
.future .legend-color { background: linear-gradient(135deg, #1dd1a1 0%, #10ac84 100%); }

/* Taahodat card color coding */
.taahodat-card.overdue {
  background: linear-gradient(135deg, #ffeaea 0%, #ffd6d6 100%);
  border-left: 4px solid #ff6b6b;
}

.taahodat-card.today {
  background: linear-gradient(135deg, #ffeef8 0%, #ffddf1 100%);
  border-left: 4px solid #ff9ff3;
}

.taahodat-card.this-week {
  background: linear-gradient(135deg, #fff9e6 0%, #fff2cc 100%);
  border-left: 4px solid #feca57;
}

.taahodat-card.this-month {
  background: linear-gradient(135deg, #e8f7ff 0%, #d4f0ff 100%);
  border-left: 4px solid #48dbfb;
}

.taahodat-card.future {
  background: linear-gradient(135deg, #e8f8f5 0%, #d1f2eb 100%);
  border-left: 4px solid #1dd1a1;
}

/* Due date badges */
.due-date-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 600;
  color: white;
}

.overdue-badge { background: linear-gradient(135deg, #ff6b6b 0%, #c23616 100%); }
.today-badge { background: linear-gradient(135deg, #ff9ff3 0%, #f368e0 100%); }
.this-week-badge { background: linear-gradient(135deg, #feca57 0%, #ff9f43 100%); }
.this-month-badge { background: linear-gradient(135deg, #48dbfb 0%, #0abde3 100%); }
.future-badge { background: linear-gradient(135deg, #1dd1a1 0%, #10ac84 100%); }

/* Text colors for modal */
.overdue-text { color: #e74c3c; font-weight: 600; }
.today-text { color: #e84393; font-weight: 600; }
.this-week-text { color: #f39c12; font-weight: 600; }
.this-month-text { color: #0984e3; font-weight: 600; }
.future-text { color: #00b894; font-weight: 600; }

.due-date-indicator {
  font-size: 0.8rem;
  color: #666;
  margin-right: 5px;
}

/* Taahodat specific styles */
.taahodat-section {
  padding: 15px;
  padding-bottom: 45px;
  padding-top: 15px;
  background-color: var(--bg);
}

.taahodat-grid {
  display: grid;
  gap: 15px;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
}

.taahodat-card {
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.taahodat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
}

.record-title {
  font-weight: 700;
  color: #2d3436;
  font-size: 1.1rem;
}

.cheque-badge {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
}

.taahodat-details {
  margin-top: 15px;
  padding: 15px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.5);
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-row .detail-label {
  font-weight: 600;
  color: #555;
  font-size: 0.9rem;
}

.detail-row .detail-value {
  color: #333;
  font-size: 0.9rem;
}

/* Responsive design */
@media (max-width: 768px) {
  .taahodat-grid {
    grid-template-columns: 1fr;
  }
  
  .taahodat-card {
    padding: 15px;
  }
  
  .search-container {
    width: 100%;
  }
  
  .legend-items {
    flex-direction: column;
    gap: 8px;
  }
}

/* Refresh button styles */
.refresh-btn {
  background: linear-gradient(135deg, #00b894 0%, #00a085 100%);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.3s ease;
}

.refresh-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 184, 148, 0.3);
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.refresh-text {
  font-size: 0.8rem;
}

/* Modal styles for cheque details */
.cheque-details {
  padding: 10px 0;
}

.cheque-details .detail-section {
  margin-bottom: 25px;
}

.cheque-details .section-title {
  font-size: 1.1rem;
  color: #333;
  margin-bottom: 15px;
  padding-bottom: 8px;
  border-bottom: 2px solid #667eea;
}

/* Filter Panel Styles */
.filter-panel {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin: 10px 0;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border-bottom: 1px solid #e0e0e0;
  background: #f8f9fa;
}

.filter-header h4 {
  margin: 0;
  color: #333;
}

.close-filter-btn {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
}

.filter-content {
  padding: 15px;
}

.filter-group {
  margin-bottom: 20px;
}

.filter-label {
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
  color: #333;
}

.date-inputs, .amount-inputs {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.date-input, .amount-input {
  flex: 1;
  min-width: 150px;
}

.date-input label, .amount-input label {
  display: block;
  margin-bottom: 5px;
  font-size: 14px;
  color: #666;
}

.persian-date-picker, .amount-picker {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.filter-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-option {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.filter-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
}

.apply-filters-btn, .clear-filters-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.apply-filters-btn {
  background: #007bff;
  color: white;
}

.clear-filters-btn {
  background: #6c757d;
  color: white;
}

/* Active Filters */
.active-filters {
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 10px;
  margin: 10px 0;
}

.active-filters-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.clear-all-filters {
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  font-size: 14px;
}

.active-filter-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.filter-tag {
  background: #007bff;
  color: white;
  padding: 4px 8px;
  border-radius: 16px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 5px;
}

.filter-tag button {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  font-size: 14px;
}

/* Filter Button with Badge */
.filter-btn {
  position: relative;
}

.filter-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  background: #dc3545;
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.charts-section {
    background: white;
    border-radius: 12px;
    padding: 20px;
    margin: 20px 0;
    box-shadow: 0 2px 10px rgba(0,0,0,0.05);
}

.charts-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.charts-title {
    font-size: 1.2rem;
    color: var(--text);
    margin: 0;
}

.chart-controls {
    display: flex;
    gap: 10px;
    align-items: center;
}

.chart-control-btn {
    padding: 8px 16px;
    background: #667eea;
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.3s;
}

.chart-control-btn:hover {
    background: #5a67d8;
}

.chart-currency-select {
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 6px;
}

.charts-container {
    margin-top: 20px;
}

.chart-type-selector {
    display: flex;
    gap: 10px;
    margin-bottom: 20px;
    flex-wrap: wrap;
}

.chart-type-btn {
    padding: 10px 20px;
    background: #f8f9fa;
    border: 2px solid #e9ecef;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s;
}

.chart-type-btn:hover {
    background: #e9ecef;
}

.chart-type-btn.active {
    background: #667eea;
    color: white;
    border-color: #667eea;
}

.chart-card {
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 10px;
    padding: 20px;
    margin-bottom: 20px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.chart-title {
    font-size: 1.1rem;
    color: #2c3e50;
    margin-bottom: 20px;
    text-align: center;
}

.chart-wrapper {
    height: 400px;
    position: relative;
}

.chart-summary {
    margin-top: 20px;
    padding: 15px;
    background: #f8f9fa;
    border-radius: 8px;
}

.summary-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #e9ecef;
}

.summary-item:last-child {
    border-bottom: none;
}

.summary-label {
    font-weight: 600;
    color: #495057;
}

.summary-value {
    color: #6c757d;
}

.summary-amount {
    font-weight: bold;
    color: #2d3436;
}

/* Print Chart Button */
.print-chart-btn {
    padding: 8px 16px;
    background: #20bf6b;
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: background 0.3s;
}

.print-chart-btn:hover {
    background: #1da65a;
}

.print-chart-text {
    font-size: 0.9rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .charts-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 15px;
    }
    
    .chart-controls {
        width: 100%;
        justify-content: space-between;
    }
    
    .chart-wrapper {
        height: 300px;
    }
    
    .chart-type-selector {
        justify-content: center;
    }
    
    .chart-type-btn {
        padding: 8px 16px;
        font-size: 0.9rem;
    }
}

/* Animation for charts */
@keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
}

.chart-card {
    animation: fadeIn 0.5s ease-out;
}

.bank-app {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: var(--bg);
}

/* =======================
   ACCOUNT SUMMARY
======================= */

.account-type {
  font-size: 16px;
  margin-left: 5px;
  opacity: .9;
}

.balance-amount {
  font-size: 18px;
  font-weight: bold;
}

/* =======================
   HEADERS
======================= */

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid var(--primary);
}

.section-title {
  font-size: 1.1rem;
  color: var(--text);
}

.back-arrow:hover {
  background: var(--card-soft);
}

/* =======================
   ACTION BAR
======================= */

.action-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 15px;
  background: var(--bg);
  border-radius: 10px;
}

.print-btn {
  background: var(--text);
  color: white;
}

.search-input {
  background: var(--card-bg);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: 8px;
}

.search-icon {
  color: var(--text-muted);
}

/* =======================
   CARDS
======================= */

.accounting-card,
.taahodat-card,
.chart-card {
  background: var(--card-bg);
  border-radius: 12px;
  border: 1px solid var(--border);
  box-shadow: var(--shadow-sm);
  transition: .2s;
}

.accounting-card:hover,
.taahodat-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

/* =======================
   TEXT COLORS
======================= */

.record-title { color: black; }

.detail-label,
.info-label {
  color: var(--text-muted);
}

.detail-value,
.info-value {
  color: black;
}

.page-info { color: var(--text-muted); }

.empty-state {
  color: var(--text-soft);
  background: var(--card-bg);
  border: 1px dashed var(--border);
}

/* =======================
   BADGES
======================= */

.debit { color: var(--danger); }
.credit { color: var(--success); }

.debit-badge {
  background: color-mix(in srgb, var(--danger) 15%, transparent);
  color: var(--danger);
}

.credit-badge {
  background: color-mix(in srgb, var(--success) 15%, transparent);
  color: var(--success);
}

/* =======================
   BUTTONS
======================= */

.refresh-btn {
  background: linear-gradient(135deg,#00b894,#00a085);
  color: white;
}

.filter-btn {
  background: var(--card-soft);
  border: 1px solid var(--border);
  color: var(--text);
}

.filter-btn:hover {
  background: color-mix(in srgb,var(--card-soft) 80%,black);
}

.apply-filters-btn {
  background: var(--primary);
  color: white;
}

.clear-filters-btn {
  background: var(--text-muted);
  color: white;
}

/* =======================
   MODALS
======================= */

.modal-overlay {
  background: rgba(0,0,0,.6);
}

.modal-content {
  background: var(--card-bg);
  border-radius: 12px;
}

.modal-header {
  background: var(--primary-gradient);
  color: white;
  border-bottom: 1px solid var(--border);
}

.modal-footer {
  border-top: 1px solid var(--border);
}

/* =======================
   FILTER PANEL
======================= */

.filter-panel {
  background: var(--card-bg);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-sm);
}

.filter-header {
  background: var(--card-soft);
}

.filter-label { color: var(--text); }

.date-picker,
.amount-picker {
  border: 1px solid var(--border);
}

/* =======================
   LEGEND
======================= */

.date-legend {
  background: var(--bg);
  border: 1px solid var(--border);
}

.legend-text { color: var(--text); }

/* =======================
   CHARTS
======================= */

.charts-section {
  background: var(--bg);
  border-radius: 12px;
  box-shadow: var(--shadow-sm);
}

.chart-title { color: var(--text); }

.chart-type-btn {
  background: var(--card-soft);
  border: 2px solid var(--border);
  color: var(--text);
}

.chart-type-btn.active {
  background: var(--primary);
  border-color: var(--primary);
  color: white;
}

.chart-summary {
  background: var(--card-soft);
}

.summary-label { color: var(--text); }
.summary-value { color: var(--text-muted); }
.summary-amount { color: var(--text); }

/* =======================
   LOADING
======================= */

.loading-state { color: var(--text-muted); }

.loading-spinner {
  border: 3px solid var(--border);
  border-top: 3px solid var(--primary);
}
</style>